import { DatePicker, LoadingButton, LocalizationProvider, TimePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { Alert, Button, Collapse, Divider, Grid, TextField } from '@mui/material';
import { format } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import uzLocale from 'date-fns/locale/uz';
import { FormikValues } from 'formik';
import { get, includes, isString } from 'lodash';
import { ComponentType, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useEdit } from '../../../hooks';
import {
  clearSchedule,
  clearScheduleRequest,
  createSchedule,
  editSchedule,
  getSchedule,
} from '../../../thunks/schedule-thunks';
import { OrganizationsSelect } from '../../organizations';
import {
  AttestationsEditValidationForm,
  FIELD_DATE,
  FIELD_ORGANIZATION,
  FIELD_TIME,
} from './attestations-edit-schema';

export interface AttestationEditFormProps {
  id?: string | number;
  date?: string;
  time?: string;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const AttestationsEditForm: ComponentType<AttestationEditFormProps> = ({
  id,
  date = new Date(),
  onCancel,
  onSubmit,
}) => {
  console.log('date', date);
  const { t, i18n } = useTranslation();
  const { user, isSuperAdmin } = useAuth();
  const {
    isLoading,
    isFetching,
    error,
    formik,
    handleFieldChange,
    handleDateProps,
    handleTimeProps,
    hasError,
    getErrorMessage,
  } = useEdit({
    id,
    entity: 'schedule',
    initialValues: {
      [FIELD_ORGANIZATION]: get(user, ['data', 'organization', 'id'], ''),
      [FIELD_DATE]: date || '',
      [FIELD_TIME]: '',
    },
    validationSchema: AttestationsEditValidationForm,
    onGetOne: getSchedule,
    onClearOne: clearSchedule,
    onClear: clearScheduleRequest,
    onCreate: createSchedule,
    onEdit: editSchedule,
    onSubmit: onSubmit,
  });

  const locale = () => {
    switch (i18n.language) {
      case 'ru':
        return ruLocale;
      default:
        return uzLocale;
    }
  };

  useEffect(() => {
    const time =
      formik?.values?.date && includes(formik?.values?.date, 'Z')
        ? format(new Date(formik.values.date), 'HH:mm')
        : '';
    formik.setValues({ ...formik.values, [FIELD_TIME]: time });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.id]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container mb={4} direction="column" spacing={1}>
        {isSuperAdmin && (
          <Grid item>
            <OrganizationsSelect
              {...formik.getFieldProps(FIELD_ORGANIZATION)}
              onChange={(e: any) => handleFieldChange(FIELD_ORGANIZATION, e)}
              error={hasError(FIELD_ORGANIZATION)}
              helperText={getErrorMessage(FIELD_ORGANIZATION)}
              label={t('organization', 'Организация')}
              placeholder={t('organization', 'Организация')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        <Grid item>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale()}>
                <DatePicker
                  {...handleDateProps(FIELD_DATE)}
                  // mask="__.__.____"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={hasError(FIELD_DATE)}
                      helperText={getErrorMessage(FIELD_DATE)}
                      label={t('attestations.date', 'Дата аттестации')}
                      placeholder={t('attestations.date.placeholder', 'Выберите дату')}
                      disabled={isLoading || isFetching}
                      fullWidth
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale()}>
                <TimePicker
                  {...handleTimeProps(FIELD_TIME)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={hasError(FIELD_TIME)}
                      helperText={getErrorMessage(FIELD_TIME)}
                      label={t('attestations.time', 'Время аттестации')}
                      placeholder={t('attestations.time.placeholder', 'Введите время аттестации')}
                      type="time"
                      disabled={isLoading || isFetching}
                      fullWidth
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isString(error) && (
        <Collapse in={!!error}>
          <Alert severity="error">{error}</Alert>
        </Collapse>
      )}
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Grid container columnSpacing={2} justifyContent="space-between">
        <Grid item>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" size="large">
              {t('cancel', 'Отмена')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isLoading || isFetching}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('action.save', 'Сохранить')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};
